import { v4 as uuidv4 } from "uuid";
import { calculateSubTotal } from "./itemCalculations";
import { isItemOutOfStock } from "./utils";
import { removePromotions } from "../features/promotions/helpers/utils";

const clearCartStorage = () => {
  localStorage.cartItems = JSON.stringify([]);
  localStorage.free_items_cart = JSON.stringify([]);
  localStorage.removeItem("cart_outlet_id");
};

const addItemToCart = (item: any, cartItems: any, cartUpdateCallBack: any) => {
  item.cart_id = uuidv4();
  item.count = 1;
  item.sub_total = calculateSubTotal(item);
  item.last_updated_at = new Date();
  cartItems.push(item);
  localStorage.cartItems = JSON.stringify(cartItems);
  cartUpdateCallBack({ items: cartItems});
  return item;
};

const addModifiedItemToCart = (
  item: any,
  cartItems: any,
  cartUpdateCallBack: any
) => {
  item.cart_id = uuidv4();
  item.sub_total = calculateSubTotal(item);
  item.last_updated_at = new Date();
  cartItems.push(item);
  localStorage.cartItems = JSON.stringify(cartItems);
  cartUpdateCallBack({ items: cartItems});
  return item;
};

const updateItemWithCartIdInCart = (
  item: any,
  cartItems: any,
  cartUpdateCallBack: any
) => {
  item.sub_total = calculateSubTotal(item);
  item.last_updated_at = new Date();
  cartItems = cartItems.map((cartItem: any) => {
    if (cartItem.cart_id === item.cart_id) {
      cartItem.count = item.count;
      cartItem.sub_total = item.sub_total;
    }
    return cartItem;
  });
  localStorage.cartItems = JSON.stringify(cartItems);
  cartUpdateCallBack({ items: cartItems});
  return item;
};

const updateItemWithIdInCart = (
  item: any,
  cartItems: any,
  cartUpdateCallBack: any
) => {
  if (!item.count || item.count === 0) {
    return removeItemWithId(item, cartItems, cartUpdateCallBack);
  }

  item.sub_total = calculateSubTotal(item);
  item.last_updated_at = new Date();
  cartItems = cartItems.map((cartItem: any) => {
    if (cartItem.id === item.id) {
      cartItem.count = item.count;
      cartItem.sub_total = item.sub_total;
    }
    return cartItem;
  });
  localStorage.cartItems = JSON.stringify(cartItems);
  cartUpdateCallBack({ items: cartItems});
  return item;
};

const removeItemWithCartId = (
  item: any,
  cartItems: any,
  cartUpdateCallBack: any
) => {
  cartItems = cartItems.filter((cartItem: any) => {
    return cartItem.cart_id !== item.cart_id;
  });
  localStorage.cartItems = JSON.stringify(cartItems);
  cartUpdateCallBack({ items: cartItems});
};

const removeItemWithId = (
  item: any,
  cartItems: any,
  cartUpdateCallBack: any
) => {
  cartItems = cartItems.filter((cartItem: any) => {
    return cartItem.id !== item.id;
  });
  localStorage.cartItems = JSON.stringify(cartItems);
  cartUpdateCallBack({ items: cartItems});
};

const isCartItemInMenu = (menuItems: any, cartItem: any) => {
  return menuItems.some((menuItem: any) => {
    if (menuItem.id === cartItem.id) {
      return true;
    }
    if (menuItem.items?.length) {
      return menuItem.items.some((itemInGroup: any) => {
        return itemInGroup.id == cartItem.id;
      });
    }
    return false;
  });
};

const validateCartItems = (
  categories: any,
  cartItems: any,
  errorCallback: any
) => {
  let itemsInMenuCount = cartItems.reduce((count: number, cartItem: any) => {
    categories.some((category: any) => {
      if (isCartItemInMenu(category.items, cartItem)) {
        count++;
        return true;
      }
      return false;
    });
    return count;
  }, 0);
  if (itemsInMenuCount === cartItems.length) {
    return cartItems;
  } else {
    errorCallback();
    clearCartStorage();
    return [];
  }
};

const findItemsForReorder = (
  pastOrderItems: any,
  categories: any,
  errorCallback: any
) => {
  let filteredPastOrderItems =
    pastOrderItems.order_details.cartItems.filter((pastOrderItem: any) => {
      let categoriesWithPastOrderItems = categories.filter((category: any) => {
        return category.items.some((categoryItem: any) => {
          if (category.has_subcategory && categoryItem.items) {
            return categoryItem.items.some((subcategoryItem: any) => {
              return subcategoryItem.id === pastOrderItem.id         
            });
          } else {
            return categoryItem.id === pastOrderItem.id;
          }
        });
      });
      return categoriesWithPastOrderItems.length > 0;
    });
  if (filteredPastOrderItems.length === pastOrderItems.order_details.cartItems.length) {
    let payloadItems = pastOrderItems.order_details.items
    //removing promotion to prevent calculation discrepancies and expired promotions
    let promotionRemovedItems = removePromotions(
      filteredPastOrderItems,
      payloadItems
    );
    
    // Sync items with current menu data to get fresh tax, surcharges, etc.
    let syncedItems = syncCartOnLocationChange(
      categories,
      promotionRemovedItems,
      errorCallback  // Show error if any items removed during sync
    );
    
    return syncedItems;
  } else {
    errorCallback();
    return [];
  }
}

const outOfStockItemsList = (cartItems: any, outOfStockItems: any) => {
  let unavailableItems:any = [];
  if (cartItems.length) {
    unavailableItems = cartItems.filter((item: any) =>
      isItemOutOfStock(item, outOfStockItems)
    );
  }
  return unavailableItems;
}

// Helper function to find a menu item by ID in categories
const findMenuItemById = (categories: any, itemId: number) => {
  for (const category of categories) {
    for (const menuItem of category.items) {
      if (menuItem.id === itemId) {
        return menuItem;
      }
      // Handle group items (items with nested items)
      if (menuItem.items?.length) {
        const groupItem = menuItem.items.find((item: any) => item.id === itemId);
        if (groupItem) return groupItem;
      }
    }
  }
  return null;
};

// Sync cart items with new menu when location changes
// Assumes items have already been validated to exist in the new menu
const syncCartOnLocationChange = (
  categories: any,
  cartItems: any,
  errorCallback?: any
) => {
  const syncedItems: any[] = [];
  
  cartItems.forEach((cartItem: any) => {
    // Find matching menu item in new location's menu
    const menuItem = findMenuItemById(categories, cartItem.id);
    
    if (menuItem) {
      // Refresh item with new location data (tax, unit_price)
      // while preserving user selections (modifiers in 'groups', 'item_notes', etc.)
      const freshItem = {
        ...cartItem,                    // Preserve selections
        unit_price: menuItem.unit_price, // Update price
        taxes: menuItem.taxes,          // Update tax info
        last_updated_at: new Date(),
      };
      freshItem.sub_total = calculateSubTotal(freshItem);
      syncedItems.push(freshItem);
    }
  });
  
  // Show error if some/all items were removed
  if (syncedItems.length < cartItems.length && errorCallback) {
    errorCallback();
  }

  localStorage.cartItems = JSON.stringify(syncedItems);
  
  return syncedItems;
};

export {
  addItemToCart,
  updateItemWithCartIdInCart,
  updateItemWithIdInCart,
  removeItemWithCartId,
  removeItemWithId,
  validateCartItems,
  findItemsForReorder,
  addModifiedItemToCart,
  outOfStockItemsList,
  syncCartOnLocationChange,
  findMenuItemById,
  clearCartStorage,
};
